import { Order } from 'src/app/models/orders';
import { TradingFacades } from 'src/app/store/order/facades/trading.facades';
import { PerformanceFacade } from 'src/app/store/performance/facade/performance.facade';
import {
  selectGeneralSelectionDate,
  selectGeneralSelectionSelectedEntities,
  selectGeneralSelectionSelectedPortfolios
} from './../../general-selection/selectors/general-selection.selectors';
import { areArraysUnequal } from './../../../utils/comparative.methods';
import { IHoldingsDataState } from './../../../models/holdings';
import { Injectable } from '@angular/core';
import { Subscription, Observable, of, combineLatest, BehaviorSubject } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { tap, take, switchMap } from 'rxjs/operators';
import { IHoldingsRequest, IAssetDrilldownRequest } from 'src/app/models/holdings';
import { IPortfolio } from 'src/app/models/portfolios';
import { ClientEntity } from 'src/app/models/clients';
import { IAppState } from 'src/app/store/state/app.state';
import * as holdingsSelectors from 'src/app/store/holdings/selectors/holdings.selectors';
import * as holdingsActions from 'src/app/store/holdings/actions/holdings.actions';
import { PortfoliosFacade } from 'src/app/store/portfolios/facade/portfolios.facade';
import {GeneralSelectionFacade} from "../../general-selection/facade/general-selection.facade";

@Injectable()
export class HoldingsFacade {
    portfoliosSubscription: Subscription;
    generalSelectionSubscription$: Subscription;
    combinedAssetAllocationSubscription: Subscription;
    tradingFacadeSubscription: Subscription;
    holdingsDataState$: Subscription;
    holdingsDataState: IHoldingsDataState;
    selectedDate: Date;
    selectedEntities: ClientEntity[];
    selectedPortfolios: IPortfolio[];
    isDataLoading = false;

    private assetAllocationsDataWithOrdersAndCash: BehaviorSubject<any[]> = new BehaviorSubject(null);
    public readonly assetAllocationsDataWithOrdersAndCash$: Observable<any[]> = this.assetAllocationsDataWithOrdersAndCash.asObservable();

    public selectedAssetPerformance$ = this.store.pipe(select(holdingsSelectors.selectAssetPerformance));
    public holdingsValuationData$ = this.store.pipe(select(holdingsSelectors.selectHoldingsValuationData));
    public holdingsExposuresData$ = this.store.pipe(select(holdingsSelectors.selectHoldingsExposuresData));
    public holdingsDataLoading$ = this.store.pipe(select(holdingsSelectors.selectHoldingsDataLoading));
    public holdingsDataError$ = this.store.pipe(select(holdingsSelectors.selectHoldingsDataError));

    constructor(
        private store: Store<IAppState>,
        private portfoliosFacade: PortfoliosFacade,
        private performanceFacade: PerformanceFacade,
        private tradingFacades: TradingFacades,
        private generalSelectionFacade: GeneralSelectionFacade) {

        // Subscription to reload data when general selection state changes.
        combineLatest([
            this.generalSelectionFacade.getSelectedEntityPortfolioState$,
            this.generalSelectionFacade.getSelectedDate()
        ])
          .subscribe(([generalState, selectedDate]) => {

            this.selectedDate = selectedDate;
            this.selectedEntities = generalState.selectedEntities;
            this.selectedPortfolios = generalState.selectedPortfolios;

            if (!this.isDataLoading && this.checkToUpdateHoldingsData()) {
              this.requestHoldingsData();
            }
          });

        this.holdingsDataState$ = this.store.pipe(select(holdingsSelectors.selectHoldingsDataState)).subscribe((holdingsDataState: IHoldingsDataState) => {
            this.holdingsDataState = holdingsDataState;
            this.isDataLoading = this.holdingsDataState.loading;
            const { error } = holdingsDataState;
            if (!this.isDataLoading && !error && this.checkToUpdateHoldingsData()) {
                this.requestHoldingsData();
            } else if (this.isDataLoading || error) {
                return;
            }
        });
        this.setupHoldingsAssetAllocationsWithOrdersAndCash();
    }

    public setupHoldingsAssetAllocationsWithOrdersAndCash() {

        if (this.combinedAssetAllocationSubscription) {
            this.combinedAssetAllocationSubscription.unsubscribe();
        }

        const combinedAssetAllocations$ = combineLatest([
            this.getHoldingsExposuresData(),
            this.performanceFacade.getPerformanceClosingValue()
        ]);

        this.combinedAssetAllocationSubscription = combinedAssetAllocations$.subscribe((values: [any[], number]) => {
            const [holdingsExposureData, performanceClosingValue] = values;

            const assetAllocationsData = holdingsExposureData.find((element: any) => {
                return (element.AssetAllocations && element.AssetAllocations.length > 0);
            });

            const assetAllocations = (!!assetAllocationsData && !!assetAllocationsData.AssetAllocations) ? assetAllocationsData.AssetAllocations : [];
            let totalAssetValue = this.CalculateTotalAssetValue(assetAllocations);

            if (totalAssetValue < performanceClosingValue) {
                // if asset allocations value is less than total portfolio value, add possible pending orders and available cash to create the full graph
                this.parseAdditionalPendingOrdersAndCash(assetAllocations, performanceClosingValue);
            } else {
                // Map assetAllocations array to Name, Y for chart
                this.assetAllocationsDataWithOrdersAndCash.next(assetAllocations);
            }
        });
    }

    private parseAdditionalPendingOrdersAndCash(assetAllocations: any, performanceClosingValue: number) {

        if (this.tradingFacadeSubscription) {
            this.tradingFacadeSubscription.unsubscribe();
        }

        let _assetAllocations = assetAllocations;
        
        //If there are no asset allocations early return because we have nothing to map anyway
        if (_assetAllocations === null || _assetAllocations === undefined || _assetAllocations.length === 0) {
            return;
        }

        this.tradingFacadeSubscription = this.tradingFacades
        .getOrders()
        .subscribe((orderData: Order[]) => {

            //If we don't have any asset allocations return.
            //It would probably be better for the getOrders call to be combined with the get asset allocations call so we 
            //always have all the data we need. This is a bit of a patch.
            if (_assetAllocations === null || _assetAllocations === undefined || _assetAllocations.length === 0) {
                return;
            }

            //Do a deep copy of asset allocations
            const updatedAssetAllocations = [..._assetAllocations];

            // Check if we have any orders - if we do add them to the asset allocation array with the classification of 'pending orders'
            if (!!orderData && orderData.length > 0) {
                updatedAssetAllocations.push(this.CreatePendingOrderAssetAllocation(orderData, performanceClosingValue));    
            }

            //Because we may have added some orders to our asset allocation array we calculate a new total to compare against the performance closing value
            let totalAssetAllocations = this.CalculateTotalAssetValue(assetAllocations);

            //If the total asset allocations is still less than the closing performance it means we have some unassigned holdings. 
            //Therefore we create an object to represent the 'left over' with the classification 'cash and cash equivalents'
            if (totalAssetAllocations < performanceClosingValue) {
                
                let restValueObject = {
                    ClassificationKey: "Cash and Cash Equivalents",
                    Total: (performanceClosingValue - totalAssetAllocations),
                    Actual: (performanceClosingValue - totalAssetAllocations) / performanceClosingValue
                };

                if (restValueObject.Actual > 0) {
                    updatedAssetAllocations.push(restValueObject);
                }
            }

            this.assetAllocationsDataWithOrdersAndCash.next(updatedAssetAllocations);

            // _assetAllocations has to be reset to null after subject has been updated with next value, this is necessary because when a user logs out
            // the order state subscription persists because holdings.facade is not destroyed when logging out.
            // Since the order subscription only gets unsubscribed manually when parseAdditionalPendingOrdersAndCash
            // is called the order subscriotion will persist and receive values when the order data is retrieved and thus the asset allocations data from the previous
             // user will be send over to whatever component subscribes to assetAllocationsDataWithOrdersAndCash$
            _assetAllocations = null;
        });
    }

    /**
     * Calculates the total of the all the asset allocations by adding them together.
     * Rounds the result to 2dp.
     */
    private CalculateTotalAssetValue(assetAllocations: any) : number{
        let totalAssetValue = 0;
        
        if (!!assetAllocations && assetAllocations.length) {
            totalAssetValue = assetAllocations.map((x: { Total: number; }) => x.Total).reduce((prev: number, next: number) => {return prev + next; }, 0);
        } 

        //Round the total asset value to 2dp. This is supposed to be a monetary value so rounding to 2dp is reasonable
        return parseFloat(totalAssetValue.toFixed(2));
    }

    private CreatePendingOrderAssetAllocation(orderData: Order[], performanceClosingValue : number) : any {
        
        //Calculate the total value of any pending orders
        const totalOrderValue = orderData.map(o => o.data.amount.value).reduce((prev, next) => { return prev + next; }, 0);

        let orderValueObject;

        // If the total exceeds 0 then create an object to represent these pending orders
        if (totalOrderValue && totalOrderValue > 0) {
            
            orderValueObject = {
                ClassificationKey: "Pending Orders",
                Total: totalOrderValue,
                Actual: totalOrderValue / performanceClosingValue
            };

            return orderValueObject
        }
    }

    private checkToUpdateHoldingsData(): boolean {
        if (!this.selectedEntities) {
            return false;
        }

        // Case 1: We have entities selected but no data
        if (this.selectedEntities.length > 0 &&
            (this.holdingsDataState.holdingsExposuresData == null ||
                this.holdingsDataState.holdingsValuationData == null)) {
            return true;
        }

        // Case 2: Check if any selection criteria has changed
        const hasSelectedEntitiesOrPortfolios = this.selectedEntities.length > 0 || this.selectedPortfolios.length > 0;

        if (!hasSelectedEntitiesOrPortfolios) {
            return false;
        }

        const datesDiff = this.holdingsDataState.selectedDate !== this.selectedDate;
        const entitiesDiff = areArraysUnequal(this.selectedEntities, this.holdingsDataState.selectedEntities, 'EntityId');
        const portfoliosDiff = areArraysUnequal(this.selectedPortfolios, this.holdingsDataState.selectedPortfolios, 'id');

        return datesDiff || entitiesDiff || portfoliosDiff;
    }


    requestHoldingsData() {
        this.loadHoldingsExposuresData(this.selectedEntities, this.selectedPortfolios);
        this.loadHoldingsValuationData(this.selectedEntities, this.selectedPortfolios);
    }

    getHoldingsValuationData(): Observable<any[]> {
        return this.store.pipe(select(holdingsSelectors.selectHoldingsValuationData), tap(res => {
            if (!res) {
                this.portfoliosSubscription = this.portfoliosFacade.getAvailablePortfolios().pipe(take(1)).subscribe((portfolioRes: IPortfolio[]) => {
                    if (portfolioRes && portfolioRes[0]) {
                        this.store.pipe(select(holdingsSelectors.selectHoldingsValuationData), take(1)).subscribe(holding => {
                            if (!holding) {
                                const params: IHoldingsRequest = {
                                    clientEntityIds: [portfolioRes[0].entityId],
                                    date: undefined
                                };
                                this.store.dispatch(new holdingsActions.GetHoldingsValuationData(params));
                            }
                        });
                    }
                });
            }
        }));
    }

    loadHoldingsValuationData(clientEntities: ClientEntity[], selectedPortfolios: IPortfolio[]) {
        const param: IHoldingsRequest = {
            date: this.selectedDate,
            clientEntityIds: clientEntities.map((clientEntity: ClientEntity) => clientEntity.EntityId),
            // Add portfolioIds if any exists
            portfolioIds: selectedPortfolios && selectedPortfolios.length > 0 ? selectedPortfolios.map((portfolio: IPortfolio) => portfolio.id) : null
        };
        this.store.dispatch(new holdingsActions.GetHoldingsValuationData(param));
    }

    getHoldingsStateData() {
        return this.store.pipe(select(holdingsSelectors.selectHoldingsDataState));
    }

    getHoldingsState(){
        return this.store.pipe(select(holdingsSelectors.getHoldingsState));
    }

    getSelectedDate() {
        return this.store.pipe(select(selectGeneralSelectionDate));
    }

    getSelectedEntities() {
        return this.store.pipe(select(selectGeneralSelectionSelectedEntities));
    }

    loadHoldingsExposuresData(clientEntities: ClientEntity[], selectedPortfolios: IPortfolio[]) {
        const param: IHoldingsRequest = {
            date: this.selectedDate,
            clientEntityIds: clientEntities.map((clientEntity: ClientEntity) => clientEntity.EntityId),
            // Add portfolioIds if any exists
            portfolioIds: selectedPortfolios && selectedPortfolios.length > 0 ? selectedPortfolios.map((portfolio: IPortfolio) => portfolio.id) : null
        };

        this.store.dispatch(new holdingsActions.GetHoldingsExposuresData(param));
    }

    getHoldingsCash(): Observable<number> {
        return this.store.pipe(select(holdingsSelectors.selectAvailableBalance));
    }

    getHoldingsExposuresData() {
        return this.store.pipe(select(holdingsSelectors.selectHoldingsExposuresData));
    }

    selectAsset(assetDrilldownRequest: IAssetDrilldownRequest) {
        this.store.dispatch(new holdingsActions.SelectAsset(assetDrilldownRequest));
    }

    hasNoHoldingsValuationsData(): Observable<boolean | null> {
        return this.store.pipe(select(holdingsSelectors.selectHoldingsValuationDataObject)).pipe(switchMap((holdingsValuationsDataObject: {
            holdingsVauationsData: any[],
            isValuationsDataLoaded: boolean
        }) => {
            const holdingsValuationsData = holdingsValuationsDataObject.holdingsVauationsData;
            const isValuationsDataLoaded = holdingsValuationsDataObject.isValuationsDataLoaded;
            /// when data is loaded BUT there's no holdings valuations data yet, the dashboard will be in onboarding state, showing
            /// a notification box and hiding most widgets
            /// when data is loaded AND there is valuations data we show the dashboard in its default state showing all widgets and hiding the onboarding notification box
            /// if holdings valuations data hasn't loaded yet, we don't want to show the notification box just yet, or any widgets that may be hidden after the data has loaded, so null is returned to handle as an in between state (and basically hide affected components and widgets)
            if (holdingsValuationsData && !holdingsValuationsData.length && isValuationsDataLoaded) {
                return of(true);
            } else if (holdingsValuationsData && holdingsValuationsData.length > 0 && isValuationsDataLoaded) {
                return of(false);
            } else if (!isValuationsDataLoaded) {
                return of(null);
            }
        }));
    }
}
